import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';
import FaqModal from 'containers/FaqModal';
import TermsOfServiceModal from 'containers/TermsOfServiceModal';
import AuthBottom from 'components/AuthBottom';
import AuthTop from 'components/AuthTop';
import Input from 'components/Input';
import ViewablePasswordField from 'components/ViewablePasswordField';
import Validators, { numbersOnlyParse } from 'utils/fieldValidators';
import { isLocationPls } from 'utils/locations';
import {
  DISPLAY_NAME_RULE_DESCRIPTION,
  PASSWORD_RULE_DESCRIPTION
} from 'utils/messages';
import {
  FORM_NAME,
  FaqLink,
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  SubmitStatus,
  TermsOfServiceLink
} from '../formConstants';
import './ExistingMemberFormPage.css';

class ExistingMemberFormPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showPassword: false,
      showTos: false,
      showFaq: false
    };

    this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    this.openTos = this.openTos.bind(this);
    this.closeTos = this.closeTos.bind(this);
    this.openFaq = this.openFaq.bind(this);
    this.closeFaq = this.closeFaq.bind(this);
  }

  togglePasswordVisibility() {
    this.setState({ showPassword: !this.state.showPassword });
  }

  openTos(event) {
    event && event.preventDefault();
    this.setState({ showTos: true });
  }

  closeTos() {
    this.setState({ showTos: false });
  }

  openFaq(event) {
    event && event.preventDefault();
    this.setState({ showFaq: true });
  }

  closeFaq() {
    this.setState({ showFaq: false });
  }

  getRewardsAccountNumberValidator = () => {
    return isLocationPls(this.props.preferredLocation)
      ? Validators.rewardsPlsNumber
      : Validators.rewardsNumber;
  };

  render() {
    const { formRenderer, preferredLocation } = this.props;

    const tosLabel = <TermsOfServiceLink onClick={this.openTos} />;

    const body = (
      <Fragment>
        <AuthTop hideLogo>
          <h2 className="sr-only focus-start" tabIndex="-1">
            Sign Up as Existing Rewards Member
          </h2>
          <div className="form-page existing-member-form-page">
            <Field
              name="existingRewardsNumber"
              component={Input}
              type="text"
              label="Rewards Account No.*"
              autoComplete="off"
              pattern="[A-Za-z0-9]*"
              wrapperClass="form-rewards-number"
              disableOnSubmitting={true}
              tooltip="Your 13-digit account number is printed on your Rewards Card"
              validate={this.getRewardsAccountNumberValidator()}
              required
            />
            <Field
              name="pin"
              component={Input}
              type="password"
              label="PIN*"
              autoComplete="off"
              inputMode="numeric"
              pattern="[0-9]*"
              wrapperClass="form-rewards-pin"
              disableOnSubmitting={true}
              parse={numbersOnlyParse}
              validate={Validators.rewardsPin}
              required
            />
            <Field
              name="email"
              component={Input}
              type="email"
              label="Email*"
              autoComplete="email username"
              disableOnSubmitting={true}
              tooltip="You will use your email address to login"
              validate={Validators.trimmedEmail}
              required
            />
            <Field
              name="confirmEmail"
              component={Input}
              type="email"
              label="Confirm Email*"
              autoComplete="email username"
              disableOnSubmitting={true}
              validate={Validators.reduxConfirmEmail}
              required
            />
            <Field
              name="password"
              component={ViewablePasswordField}
              showPassword={this.state.showPassword}
              toggleVisibility={this.togglePasswordVisibility}
              label="Password*"
              autoComplete="new-password"
              disableOnSubmitting={true}
              tooltip={PASSWORD_RULE_DESCRIPTION}
              validate={Validators.password}
              required
            />
            <Field
              name="confirmPassword"
              component={ViewablePasswordField}
              showPassword={this.state.showPassword}
              label="Confirm Password*"
              autoComplete="new-password"
              disableOnSubmitting={true}
              validate={Validators.reduxConfirmPassword}
              required
            />
            <Field
              name="displayName"
              component={Input}
              type="text"
              label="Display Name*"
              autoComplete="nickname"
              disableOnSubmitting={true}
              tooltip={`This is the name that will be displayed to others when you post on the site. ${DISPLAY_NAME_RULE_DESCRIPTION}`}
              validate={Validators.trimmedDisplayName}
              required
            />
            <Field
              name="eventCode"
              component={Input}
              type="text"
              label="Event Code"
              autoComplete="off"
              disableOnSubmitting={true}
              tooltip="If you are signing up as part of a promotional event, enter the code here"
            />
            {isLocationPls(preferredLocation) && (
              <Field
                name="gender"
                component={Input}
                type="select"
                label="Gender*"
                placeholder="Select"
                disableOnSubmitting={true}
                validate={Validators.required}
                required
              >
                {GENDER_OPTIONS}
              </Field>
            )}
            <Field
              name="preferredLanguage"
              component={Input}
              type="select"
              label="Language*"
              placeholder="Select"
              disableOnSubmitting={true}
              validate={Validators.required}
              required
            >
              {LANGUAGE_OPTIONS}
            </Field>
            {!isLocationPls(preferredLocation) && (
              <div className="input-container" />
            )}
            <Field
              name="agreeToTOS"
              component={Input}
              type="checkbox"
              label="Terms Of Service"
              labelNode={tosLabel}
              disableOnSubmitting={true}
              wrapperClass="form-tos"
              validate={Validators.termsOfService}
              required
            />
            <TermsOfServiceModal
              onHide={this.closeTos}
              show={this.state.showTos}
            />
            <FaqModal onHide={this.closeFaq} show={this.state.showFaq} />
          </div>
        </AuthTop>
        <AuthBottom>
          <button
            className="btn button-color-green"
            disabled={this.props.submitting || !this.props.valid}
            type="submit"
          >
            {this.props.submitText}
            <SubmitStatus submitting={this.props.submitting} />
          </button>
          <FaqLink onClick={this.openFaq} />
        </AuthBottom>
      </Fragment>
    );

    return formRenderer(this.props, body);
  }
}
const formSelector = formValueSelector(FORM_NAME);
const mapStateToProps = createStructuredSelector({
  preferredLocation: state => formSelector(state, 'preferredLocation')
});

const reduxFormPage = reduxForm({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
})(ExistingMemberFormPage);

const withConnect = connect(mapStateToProps);

reduxFormPage.FormFields = [
  'existingRewardsNumber',
  'pin',
  'email',
  'displayName',
  'password',
  'confirmPassword',
  'eventCode',
  'agreeToTOS'
];

export default withConnect(reduxFormPage);
